import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styles from './InvoicePage.module.css';

// ============================================
// EDITABLE INVOICE DATA - Edit values below
// ============================================
const INVOICE_DATA = {
  seller: {
    name: 'Jamelly',
    email: 'hello@jamelly.art',
    instagram: 'https://instagram.com/jamelly.art'
  },
  collector: {
    name: 'Kirsten Poe Hill',
    email: 'kph@noelleelaine.com'
  },
  invoice: {
    number: 'COM-2025-001',
    date: 'December 16, 2025'
  },
  artwork: {
    title: 'The Sun Sets to Rise',
    size: '24 x 48 inches',
    year: 2025,
    edition: '1 of 1 (Original)',
    commissioned: 'YES'
  },
  pricing: {
    artworkAmount: 2500,
    shippingAmount: 150,
    discount: {
      type: 'amount', // 'none' | 'amount' | 'percent'
      value: 2000,
      code: 'YOUKNOWMYHUSBAND'
    }
  },
  payment: {
    method: 'Zelle',
    zelleNumber: '929.362.8515',
    terms: 'Payment is due upon receipt of the artwork.'
  },
  notes: 'A signed Certificate of Authenticity will be included with your piece.',
  gratitude: 'Thank you for giving this work a home. I never take that lightly.\n\nWith gratitude,\nJamelly'
};

// ============================================
// COMPONENT
// ============================================
const Invoice_COM_2025_001 = () => {
  const [copied, setCopied] = useState(false);

  // Calculate totals
  const subtotal = INVOICE_DATA.pricing.artworkAmount;
  let discountAmount = 0;
  
  if (INVOICE_DATA.pricing.discount.type === 'percent') {
    discountAmount = subtotal * (INVOICE_DATA.pricing.discount.value / 100);
  } else if (INVOICE_DATA.pricing.discount.type === 'amount') {
    discountAmount = INVOICE_DATA.pricing.discount.value;
  }
  
  const total = subtotal + INVOICE_DATA.pricing.shippingAmount - discountAmount;

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Invoice {INVOICE_DATA.invoice.number} | Jamelly</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className={styles.invoiceContainer}>
        <div className={styles.invoicePaper}>
          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button onClick={handlePrint} className={styles.button}>
              Print / Save
            </button>
            <button onClick={handleCopyLink} className={styles.button}>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>

          {/* Header */}
          <div className={styles.header}>
            <div className={styles.sellerInfo}>
              <h1 className={styles.sellerName}>{INVOICE_DATA.seller.name}</h1>
            </div>
            <div className={styles.sellerContact}>
              <a href={`mailto:${INVOICE_DATA.seller.email}`} className={styles.email}>
                {INVOICE_DATA.seller.email}
              </a>
            </div>
          </div>

          {/* Invoice Meta and Bill To */}
          <div className={styles.metaSection}>
            <div className={styles.billTo}>
              <h2 className={styles.sectionTitle}>Bill To</h2>
              <p className={styles.collectorName}>{INVOICE_DATA.collector.name}</p>
              <a href={`mailto:${INVOICE_DATA.collector.email}`} className={styles.collectorEmail}>
                {INVOICE_DATA.collector.email}
              </a>
            </div>
            <div className={styles.invoiceMeta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Invoice Number:</span>
                <span className={styles.metaValue}>{INVOICE_DATA.invoice.number}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Invoice Date:</span>
                <span className={styles.metaValue}>{INVOICE_DATA.invoice.date}</span>
              </div>
            </div>
          </div>

          {/* Artwork Details */}
          <div className={styles.artworkSection}>
            <h2 className={styles.sectionTitle}>Artwork Details</h2>
            <div className={styles.artworkGrid}>
              <div className={styles.artworkRow}>
                <span className={styles.artworkLabel}>Title:</span>
                <span className={styles.artworkValue}>{INVOICE_DATA.artwork.title}</span>
              </div>
              <div className={styles.artworkRow}>
                <span className={styles.artworkLabel}>Size:</span>
                <span className={styles.artworkValue}>{INVOICE_DATA.artwork.size}</span>
              </div>
              <div className={styles.artworkRow}>
                <span className={styles.artworkLabel}>Year:</span>
                <span className={styles.artworkValue}>{INVOICE_DATA.artwork.year}</span>
              </div>
              <div className={styles.artworkRow}>
                <span className={styles.artworkLabel}>Edition:</span>
                <span className={styles.artworkValue}>{INVOICE_DATA.artwork.edition}</span>
              </div>
              <div className={styles.artworkRow}>
                <span className={styles.artworkLabel}>Commissioned:</span>
                <span className={styles.artworkValue}>{INVOICE_DATA.artwork.commissioned}</span>
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className={styles.lineItemsSection}>
            <table className={styles.lineItemsTable}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Description</th>
                  <th className={styles.tableHeaderAmount}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.tableCell}>Artwork</td>
                  <td className={styles.tableCellAmount}>
                    ${INVOICE_DATA.pricing.artworkAmount.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className={styles.tableCell}>Shipping</td>
                  <td className={styles.tableCellAmount}>
                    ${INVOICE_DATA.pricing.shippingAmount.toFixed(2)}
                  </td>
                </tr>
                {discountAmount > 0 && (
                  <tr>
                    <td className={styles.tableCell}>
                      Discount
                      {INVOICE_DATA.pricing.discount.code && 
                        ` (Code: ${INVOICE_DATA.pricing.discount.code})`}
                      {INVOICE_DATA.pricing.discount.type === 'percent' && 
                        ` (${INVOICE_DATA.pricing.discount.value}%)`}
                    </td>
                    <td className={styles.tableCellAmount}>
                      -${discountAmount.toFixed(2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className={styles.totalsSection}>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Subtotal:</span>
              <span className={styles.totalValue}>${subtotal.toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Discount:</span>
                <span className={styles.totalValue}>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Shipping:</span>
              <span className={styles.totalValue}>${INVOICE_DATA.pricing.shippingAmount.toFixed(2)}</span>
            </div>
            <div className={styles.totalRowFinal}>
              <span className={styles.totalLabelFinal}>Total:</span>
              <span className={styles.totalValueFinal}>${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Information */}
          <div className={styles.paymentSection}>
            <h2 className={styles.sectionTitle}>Payment Information</h2>
            <p className={styles.paymentMethod}>
              <strong>Method:</strong> {INVOICE_DATA.payment.method}
            </p>
            <p className={styles.paymentDetails}>
              <strong>Zelle Number:</strong> {INVOICE_DATA.payment.zelleNumber}
            </p>
            <p className={styles.paymentTerms}>{INVOICE_DATA.payment.terms}</p>
          </div>

          {/* Notes */}
          <div className={styles.notesSection}>
            <h2 className={styles.sectionTitle}>Notes</h2>
            <p className={styles.notesText}>{INVOICE_DATA.notes}</p>
          </div>

          {/* Gratitude Sign-off */}
          <div className={styles.gratitudeSection}>
            <p className={styles.gratitudeText}>
              {INVOICE_DATA.gratitude.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < INVOICE_DATA.gratitude.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <a 
              href={INVOICE_DATA.seller.instagram} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.instagramLink}
            >
              Instagram: @jamelly.art
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoice_COM_2025_001;

